import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import AiWorkspaceService, {
  AiPromptConfigData,
  AiPromptConfigTemplate,
} from "../../_services/AiWorkspaceService";
import AiSubscriptionsService, {
  SupportedModel,
} from "../../_services/AiSubscriptionsService";
import ApiService from "../../_services/ApiService";
import RubricService, { Rubric } from "../../_services/RubricService";
import StudyResourceService from "../../_services/StudyResourceService";
import "./ai-workspace-config.css";

interface TestResult {
  response: string;
  tokenUsage: any;
  processingTimeMs: number;
}

interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: "socratic",
    name: "Socratic Tutor",
    description: "Guides students through questions drawn from the study material. References specific content to frame questions.",
    prompt: `You are a Socratic tutor helping students understand the attached study material. You have access to the course materials and should use them to guide the conversation.

Guidelines:
- Ask one focused question at a time to probe the student's understanding of the material
- When referencing concepts, cite the specific source material (e.g., "In the section on X...")
- When a student gives an incomplete answer, refer them to the relevant part of the material
- Affirm correct reasoning before building on it
- If the student is stuck, quote or paraphrase a relevant passage as a hint
- Keep responses concise and conversational
- Stay focused on the attached study material and learning objectives`,
  },
  {
    id: "explainer",
    name: "Content Explainer",
    description: "Explains concepts directly from the study material, citing specific passages and pages.",
    prompt: `You are a knowledgeable tutor who explains the attached study material to students. You have access to the course content and should reference it directly in your responses.

Guidelines:
- When explaining a concept, cite the specific source and page number from the course materials
- Quote or paraphrase relevant passages to support your explanations
- Break down complex ideas from the material into simpler terms
- Use examples that relate to the content in the study material
- If the student asks about something not covered in the material, let them know and offer what related content is available
- Encourage students to re-read specific sections for deeper understanding
- Keep explanations clear and grounded in the provided material`,
  },
  {
    id: "discussion",
    name: "Discussion Facilitator",
    description: "Balanced discussion that references the material while encouraging critical thinking.",
    prompt: `You are a discussion facilitator helping students engage critically with the attached study material. You have access to the course content and should reference it throughout the conversation.

Guidelines:
- Begin by asking the student what they found most interesting or challenging in the material
- Reference specific passages, examples, or data from the study material in your responses
- Ask students to connect ideas from different parts of the material
- Encourage students to question assumptions made in the text
- When a student makes a claim, ask them to support it with evidence from the material
- Offer alternative interpretations of the material to deepen understanding
- Summarize key takeaways from the material at appropriate points in the conversation`,
  },
  {
    id: "quiz",
    name: "Concept Checker",
    description: "Tests understanding with questions drawn from the study material, provides feedback with citations.",
    prompt: `You are an assessment tutor who checks students' understanding of the attached study material. You have access to the course content and should base all questions on it.

Guidelines:
- Ask questions that test comprehension of specific concepts from the study material
- After the student answers, provide feedback that references the relevant section of the material
- If the student answers incorrectly, quote or paraphrase the relevant passage to help them understand
- Progress from basic recall questions to deeper analysis questions about the material
- Cover the key topics and concepts present in the available course content
- Keep track of which areas the student understands well and which need more work
- At the end, summarize which concepts from the material the student has mastered and which to review`,
  },
];

// Preset configurations for content boundaries
const BOUNDARY_PRESETS: Record<string, {
  withinResource: { behavior: string; tone: string };
  withinModule: { behavior: string; tone: string };
  subjectRelated: { behavior: string; tone: string };
  unrelated: { behavior: string; tone: string };
}> = {
  strict: {
    withinResource: { behavior: "allow", tone: "encouraging" },
    withinModule: { behavior: "redirect", tone: "gentle" },
    subjectRelated: { behavior: "redirect", tone: "firm" },
    unrelated: { behavior: "block", tone: "firm" },
  },
  balanced: {
    withinResource: { behavior: "allow", tone: "encouraging" },
    withinModule: { behavior: "allow", tone: "encouraging" },
    subjectRelated: { behavior: "redirect", tone: "gentle" },
    unrelated: { behavior: "redirect", tone: "neutral" },
  },
  exploratory: {
    withinResource: { behavior: "allow", tone: "encouraging" },
    withinModule: { behavior: "allow", tone: "encouraging" },
    subjectRelated: { behavior: "allow", tone: "gentle" },
    unrelated: { behavior: "redirect", tone: "gentle" },
  },
};

const DEFAULT_STUDENT_INSTRUCTIONS = `Read through the assigned study material before starting this conversation. Then discuss your understanding with the AI tutor.

Tips:
- Start by summarizing what you understood from the material
- Ask questions about concepts that are unclear
- Try to answer the tutor's questions in your own words
- Take your time — there is no rush`;

const DEFAULT_REFLECTION_PROMPT = `Reflect on your conversation with the AI tutor:
- What key concepts did you explore?
- What surprised you or challenged your initial understanding?
- What questions do you still have that you want to explore further?
- How has your understanding of the material changed?`;

const AiWorkspaceConfig: React.FC = () => {
  const history = useHistory();
  // URL params
  const urlParams = new URLSearchParams(window.location.search);
  const resourceId = Number(urlParams.get("resourceId")) || 0;
  const resourceName = urlParams.get("resourceName") || "Study Resource";
  const mode = urlParams.get("mode"); // "global-doubt" | "global-generic" | "global-resource-default" | "global-atlas-counselor" | null
  const isGlobalMode = mode?.startsWith("global-") || false;
  const globalConfigType: "doubt" | "generic" | "resource-default" | "atlas-counselor" | null =
    mode === "global-doubt" ? "doubt"
    : mode === "global-generic" ? "generic"
    : mode === "global-resource-default" ? "resource-default"
    : mode === "global-atlas-counselor" ? "atlas-counselor"
    : null;
  // resource-default shows all workspace fields (instructions, turns, reflection)
  const isResourceDefault = globalConfigType === "resource-default";
  const isCounselor = globalConfigType === "atlas-counselor";

  // Config state
  const [config, setConfig] = useState<AiPromptConfigData | null>(null);
  const [isNew, setIsNew] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [instructionsForStudent, setInstructionsForStudent] = useState("");
  const [maxTurns, setMaxTurns] = useState(10);
  const [maxTokensPerResponse, setMaxTokensPerResponse] = useState(2048);
  const [temperature, setTemperature] = useState(0.7);
  // Empty string = inherit the tenant default model (set at /ai-subscriptions).
  // A specific id pins this persona/feature to that model regardless of the
  // tenant default.
  const [model, setModel] = useState("");
  const [supportedModels, setSupportedModels] = useState<SupportedModel[]>([]);
  const [requireReflection, setRequireReflection] = useState(false);
  const [reflectionPrompt, setReflectionPrompt] = useState("");
  // Persona-scoped knowledge base lesson plan id (counselor mode only).
  // Lives on the ai_prompt_config row so the ilearn client never has to
  // know a tenant-specific lesson plan id.
  const [knowledgeBaseLessonPlanId, setKnowledgeBaseLessonPlanId] = useState<number | "">("");

  // Guardrails state
  const [blockedTopics, setBlockedTopics] = useState("");
  const [requiredTopics, setRequiredTopics] = useState("");
  const [maxStudentMessageLength, setMaxStudentMessageLength] = useState(2000);

  // Content Boundaries state
  type BoundaryPreset = "strict" | "balanced" | "exploratory" | "custom";
  type BoundaryBehavior = "allow" | "redirect" | "block";
  type BoundaryTone = "encouraging" | "gentle" | "neutral" | "firm";

  interface BoundaryLevel {
    behavior: BoundaryBehavior;
    tone: BoundaryTone;
    customMessage?: string;
  }

  interface CustomBoundaries {
    withinResource: BoundaryLevel;
    withinModule: BoundaryLevel;
    subjectRelated: BoundaryLevel;
    unrelated: BoundaryLevel;
  }

  const DEFAULT_CUSTOM_BOUNDARIES: CustomBoundaries = {
    withinResource: { behavior: "allow", tone: "encouraging" },
    withinModule: { behavior: "allow", tone: "encouraging" },
    subjectRelated: { behavior: "redirect", tone: "gentle" },
    unrelated: { behavior: "redirect", tone: "neutral" },
  };

  const [boundaryPreset, setBoundaryPreset] = useState<BoundaryPreset>("balanced");
  const [customBoundaries, setCustomBoundaries] = useState<CustomBoundaries>(DEFAULT_CUSTOM_BOUNDARIES);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"config" | "test">("config");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Test state
  const [testMessage, setTestMessage] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // Rubric state
  const [linkedRubric, setLinkedRubric] = useState<Rubric | null>(null);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [rubricLoading, setRubricLoading] = useState(false);
  const [editingRubric, setEditingRubric] = useState(false);
  const [updatingRubric, setUpdatingRubric] = useState(false);

  // RAG state
  interface RagLessonPlan {
    lessonPlanId: number;
    lessonPlanName: string;
    ragStatus: string | null;
    resourceEmbedded: boolean;
    chunksCreated: number;
    vectorsStored: number;
  }
  const [ragStatus, setRagStatus] = useState<{
    isRAGified: boolean;
    lessonPlans: RagLessonPlan[];
    message?: string;
  } | null>(null);
  const [ragLoading, setRagLoading] = useState(false);
  const [ragifying, setRagifying] = useState(false);
  const [ragifyTargetLpId, setRagifyTargetLpId] = useState<number | null>(null);

  // Validation state
  const [showValidationDetails, setShowValidationDetails] = useState(false);

  // Template state
  const [templates, setTemplates] = useState<{
    own: AiPromptConfigTemplate[];
    shared: AiPromptConfigTemplate[];
  }>({ own: [], shared: [] });
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateIsShared, setTemplateIsShared] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Template sync state
  const [linkedTemplate, setLinkedTemplate] = useState<AiPromptConfigTemplate | null>(null);
  const [templateUpdateAvailable, setTemplateUpdateAvailable] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingFromTemplate, setSyncingFromTemplate] = useState(false);
  const [unlinkingTemplate, setUnlinkingTemplate] = useState(false);

  // AI Analysis state
  const [aiAnalysisIssues, setAiAnalysisIssues] = useState<Array<{
    id: string;
    severity: "error" | "warning" | "info";
    message: string;
    suggestion?: string;
  }>>([]);
  const [aiOverallAssessment, setAiOverallAssessment] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showScoreInfo, setShowScoreInfo] = useState(false);

  // Validation Rules Engine
  interface ValidationIssue {
    id: string;
    severity: "error" | "warning" | "info";
    message: string;
    suggestion?: string;
    isAiIssue?: boolean;
  }

  const validateConfig = (): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];

    // Rule 1: TOPIC_CONFLICT - Blocked topic appears in required topics
    if (blockedTopics.trim() && requiredTopics.trim()) {
      const blocked = blockedTopics.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
      const required = requiredTopics.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
      const conflicts = blocked.filter((t) => required.includes(t));
      if (conflicts.length > 0) {
        issues.push({
          id: "TOPIC_CONFLICT",
          severity: "error",
          message: `"${conflicts.join('", "')}" appears in both blocked and required topics`,
        });
      }
    }

    // Rule 2: REFLECTION_INCOMPLETE - Reflection required but no prompt
    if (requireReflection && !reflectionPrompt.trim()) {
      issues.push({
        id: "REFLECTION_INCOMPLETE",
        severity: "error",
        message: "Reflection is required but no reflection prompt is provided",
      });
    }

    // Rule 3: EMPTY_SYSTEM_PROMPT - System prompt too short
    if (systemPrompt.trim().length > 0 && systemPrompt.trim().length < 50) {
      issues.push({
        id: "EMPTY_SYSTEM_PROMPT",
        severity: "warning",
        message: "System prompt is very short - consider adding more context for better AI responses",
      });
    }

    // Rule 4: TEMP_BOUNDARY_MISMATCH - High temperature with strict boundaries
    if (temperature > 0.8 && boundaryPreset === "strict") {
      issues.push({
        id: "TEMP_BOUNDARY_MISMATCH",
        severity: "warning",
        message: "High temperature (>0.8) may produce creative responses that conflict with strict content boundaries",
      });
    }

    // Rule 5: TONE_MISMATCH - System prompt tone vs content boundary tones
    const promptLower = systemPrompt.toLowerCase();
    const hasEncouragingPrompt = promptLower.includes("encouraging") ||
                                  promptLower.includes("friendly") ||
                                  promptLower.includes("supportive") ||
                                  promptLower.includes("open discussion");
    const activeLevels = boundaryPreset === "custom" ? customBoundaries : BOUNDARY_PRESETS[boundaryPreset];
    const hasFirmTone = activeLevels && (
      activeLevels.withinModule?.tone === "firm" ||
      activeLevels.subjectRelated?.tone === "firm" ||
      activeLevels.unrelated?.tone === "firm"
    );
    if (hasEncouragingPrompt && hasFirmTone) {
      issues.push({
        id: "TONE_MISMATCH",
        severity: "warning",
        message: "Your system prompt encourages open discussion but content boundaries use 'firm' tone - this may create inconsistent responses",
      });
    }

    // Rule 6: NO_GUARDRAILS - No topics with non-exploratory preset
    if (!blockedTopics.trim() && !requiredTopics.trim() && boundaryPreset !== "exploratory") {
      issues.push({
        id: "NO_GUARDRAILS",
        severity: "info",
        message: "Consider adding blocked or required topics to help focus the conversation",
      });
    }

    // Rule 7: CUSTOM_MSG_MISSING - Custom preset without custom messages
    if (boundaryPreset === "custom") {
      const hasCustomMessages =
        customBoundaries.withinModule?.customMessage ||
        customBoundaries.subjectRelated?.customMessage ||
        customBoundaries.unrelated?.customMessage;
      const hasRedirectOrBlock =
        customBoundaries.withinModule?.behavior !== "allow" ||
        customBoundaries.subjectRelated?.behavior !== "allow" ||
        customBoundaries.unrelated?.behavior !== "allow";
      if (hasRedirectOrBlock && !hasCustomMessages) {
        issues.push({
          id: "CUSTOM_MSG_MISSING",
          severity: "info",
          message: "Custom boundary levels work better with custom redirect messages - consider adding specific guidance",
        });
      }
    }

    return issues;
  };

  const validationIssues = validateConfig();

  // Combine rule-based and AI issues
  const allIssues: ValidationIssue[] = [
    ...validationIssues,
    ...aiAnalysisIssues.map((issue) => ({ ...issue, isAiIssue: true })),
  ];

  // Calculate consistency score
  const calculateScore = (issues: ValidationIssue[]): number => {
    const errorCount = issues.filter((i) => i.severity === "error").length;
    const warningCount = issues.filter((i) => i.severity === "warning").length;
    const infoCount = issues.filter((i) => i.severity === "info").length;
    const score = 100 - (errorCount * 25) - (warningCount * 10) - (infoCount * 2);
    return Math.max(0, score);
  };

  const consistencyScore = calculateScore(allIssues);

  const getScoreColor = (score: number): string => {
    if (score >= 90) return "#10b981"; // green
    if (score >= 70) return "#3b82f6"; // blue
    if (score >= 50) return "#f59e0b"; // yellow
    return "#ef4444"; // red
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 90) return "Excellent";
    if (score >= 70) return "Good";
    if (score >= 50) return "Needs Attention";
    return "Issues Found";
  };

  useEffect(() => {
    if (resourceId) {
      loadConfig();
      loadRubricInfo();
      loadAllRubrics();
      loadRagStatus();
    } else if (isGlobalMode) {
      loadGlobalConfig();
    } else {
      setLoading(false);
      setError("No resourceId provided in URL parameters.");
    }
  }, [resourceId]);

  // The model dropdown sources its options from the same backend constant
  // (SUPPORTED_AI_MODELS) that powers /ai-subscriptions, so the two surfaces
  // never drift. Empty list on failure is fine — the picker still shows
  // "Use tenant default" and any legacy value already saved on the row.
  useEffect(() => {
    let cancelled = false;
    AiSubscriptionsService.getSupportedModels().then((result) => {
      if (cancelled) return;
      if (result.success && result.data?.models) {
        setSupportedModels(result.data.models);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRagStatus = async () => {
    setRagLoading(true);
    try {
      const response = await ApiService.getResourceRagStatus(resourceId);
      const data = response.data as any;
      if (data?.success) {
        setRagStatus({
          isRAGified: data.isRAGified,
          lessonPlans: data.lessonPlans || [],
          message: data.message,
        });
      }
    } catch (err) {
      console.error("Failed to load RAG status:", err);
    } finally {
      setRagLoading(false);
    }
  };

  const handleRagify = async (lessonPlanId: number) => {
    setRagifying(true);
    try {
      const response = await ApiService.ragifyLessonPlan(lessonPlanId);
      const data = response.data as any;
      if (data?.success) {
        setSuccessMessage("RAGification completed successfully!");
        // Refresh RAG status
        await loadRagStatus();
      } else {
        setError(data?.message || "RAGification failed");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err.message || "RAGification failed");
    } finally {
      setRagifying(false);
      setRagifyTargetLpId(null);
    }
  };

  const loadAllRubrics = async () => {
    try {
      const response = await RubricService.getAllRubrics();
      if (response.success && response.data?.rubrics) {
        setRubrics(response.data.rubrics);
      }
    } catch (err) {
      console.error("Failed to load rubrics:", err);
    }
  };

  const loadRubricInfo = async () => {
    setRubricLoading(true);
    try {
      // First get the study resource to find rubricId
      const resourceResponse = await StudyResourceService.getStudyResourceById(resourceId);
      if (resourceResponse.success && resourceResponse.data?.rubricId) {
        // Fetch the rubric details
        const rubricResponse = await RubricService.getRubricById(resourceResponse.data.rubricId);
        if (rubricResponse.success && rubricResponse.data) {
          setLinkedRubric(rubricResponse.data);
        }
      }
    } catch (err) {
      console.error("Failed to load rubric info:", err);
    } finally {
      setRubricLoading(false);
    }
  };

  const handleRubricChange = async (rubricId: number | null) => {
    setUpdatingRubric(true);
    try {
      await StudyResourceService.updateStudyResource(resourceId, { rubricId });
      // Update local state
      if (rubricId) {
        const selectedRubric = rubrics.find(r => r.id === rubricId);
        setLinkedRubric(selectedRubric || null);
      } else {
        setLinkedRubric(null);
      }
      setEditingRubric(false);
      setSuccessMessage("Rubric updated successfully");
    } catch (err: any) {
      setError(err.message || "Failed to update rubric");
    } finally {
      setUpdatingRubric(false);
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AiWorkspaceService.getConfigByResource(resourceId);
      if (response.success && response.data) {
        const c = response.data;
        setConfig(c);
        setName(c.name);
        setSystemPrompt(c.systemPrompt);
        setInstructionsForStudent(c.instructionsForStudent || "");
        setMaxTurns(c.maxTurns);
        setMaxTokensPerResponse(c.maxTokensPerResponse);
        setTemperature(parseFloat(c.temperature));
        setModel(c.model);
        setRequireReflection(c.requireReflection === 1);
        setReflectionPrompt(c.reflectionPrompt || "");
        if (c.guardrails) {
          setBlockedTopics(
            (c.guardrails.blockedTopics || []).join(", ")
          );
          setRequiredTopics(
            (c.guardrails.requiredTopics || []).join(", ")
          );
          setMaxStudentMessageLength(
            c.guardrails.maxStudentMessageLength || 2000
          );
          // Load content boundaries
          if (c.guardrails.contentBoundaries) {
            setBoundaryPreset(c.guardrails.contentBoundaries.preset || "balanced");
            if (c.guardrails.contentBoundaries.levels) {
              setCustomBoundaries(c.guardrails.contentBoundaries.levels);
            }
          }
        }
        setIsNew(false);

        // Check for linked template
        if (c.sourceTemplateId) {
          try {
            const templateResponse = await AiWorkspaceService.getTemplateById(c.sourceTemplateId);
            if (templateResponse.success && templateResponse.data) {
              setLinkedTemplate(templateResponse.data);
              // Check if template has been updated since last sync
              const currentVersion = templateResponse.data.version;
              const appliedVersion = c.templateVersionApplied || 0;
              setTemplateUpdateAvailable(currentVersion > appliedVersion);
            }
          } catch (templateErr) {
            console.error("Failed to load linked template:", templateErr);
          }
        } else {
          setLinkedTemplate(null);
          setTemplateUpdateAvailable(false);
        }
      } else {
        // Config not found - show create mode
        console.log(`No config found for resource ${resourceId}:`, response.error);
        setIsNew(true);
        setName(resourceName);
      }
    } catch (err) {
      // Unexpected error - still show create mode but log the error
      console.error(`Error loading config for resource ${resourceId}:`, err);
      setIsNew(true);
      setName(resourceName);
    } finally {
      setLoading(false);
    }
  };

  const globalDefaultName = globalConfigType === "generic" ? "Global AI Assistant Config"
    : globalConfigType === "resource-default" ? "Default AI Workspace Config"
    : globalConfigType === "atlas-counselor" ? "Atlas Counselor Config"
    : "Global Doubt Config";

  const loadGlobalConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AiWorkspaceService.getGlobalConfig(globalConfigType!);
      if (result.success && result.data) {
        const d = result.data;
        setConfig(d);
        setName(d.name || globalDefaultName);
        setSystemPrompt(d.systemPrompt || "");
        setModel(d.model || "");
        setTemperature(parseFloat(d.temperature) || 0.7);
        setMaxTokensPerResponse(d.maxTokensPerResponse || 2048);
        if (d.instructionsForStudent) setInstructionsForStudent(d.instructionsForStudent);
        if (d.maxTurns) setMaxTurns(d.maxTurns);
        if (d.requireReflection !== undefined) setRequireReflection(d.requireReflection === 1);
        if (d.reflectionPrompt) setReflectionPrompt(d.reflectionPrompt);
        setKnowledgeBaseLessonPlanId(d.knowledgeBaseLessonPlanId ?? "");
        if (d.guardrails) {
          setBlockedTopics((d.guardrails.blockedTopics || []).join(", "));
          setRequiredTopics((d.guardrails.requiredTopics || []).join(", "));
          setMaxStudentMessageLength(d.guardrails.maxStudentMessageLength || 2000);
          if (d.guardrails.contentBoundaries) {
            setBoundaryPreset(d.guardrails.contentBoundaries.preset || "balanced");
            if (d.guardrails.contentBoundaries.levels) {
              setCustomBoundaries(d.guardrails.contentBoundaries.levels);
            }
          }
        }
        setIsNew(false);
      } else {
        // No global config yet — create mode
        setIsNew(true);
        setName(globalDefaultName);
      }
    } catch (err) {
      console.error("Error loading global AI config:", err);
      setIsNew(true);
      setName(globalDefaultName);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    const guardrails: any = {};
    if (blockedTopics.trim()) {
      guardrails.blockedTopics = blockedTopics
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }
    if (requiredTopics.trim()) {
      guardrails.requiredTopics = requiredTopics
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }
    if (maxStudentMessageLength) {
      guardrails.maxStudentMessageLength = maxStudentMessageLength;
    }

    // Add content boundaries
    guardrails.contentBoundaries = {
      preset: boundaryPreset,
      levels: boundaryPreset === "custom" ? customBoundaries : undefined,
    };

    try {
      if (isGlobalMode) {
        const globalPayload: any = {
          name: name || globalDefaultName,
          systemPrompt,
          guardrails: Object.keys(guardrails).length > 0 ? guardrails : undefined,
          maxTokensPerResponse,
          temperature,
          model,
        };
        // resource-default also includes workspace-specific fields
        if (isResourceDefault) {
          globalPayload.instructionsForStudent = instructionsForStudent || undefined;
          globalPayload.maxTurns = maxTurns;
          globalPayload.requireReflection = requireReflection;
          globalPayload.reflectionPrompt = reflectionPrompt || undefined;
        }
        // counselor binds its RAG knowledge base via lesson_plan id
        if (isCounselor) {
          globalPayload.knowledgeBaseLessonPlanId =
            knowledgeBaseLessonPlanId === "" ? null : Number(knowledgeBaseLessonPlanId);
        }
        const result = await AiWorkspaceService.updateGlobalConfig(globalConfigType!, globalPayload);

        if (result.success) {
          setIsNew(false);
          setSuccessMessage("Global AI config saved successfully.");
          // Reload to get the config id
          if (isNew) await loadGlobalConfig();
        } else {
          setError(result.error || "Failed to save global config");
        }
      } else if (isNew) {
        const response = await AiWorkspaceService.createConfig({
          studyResourceId: resourceId,
          name,
          systemPrompt,
          instructionsForStudent: instructionsForStudent || undefined,
          maxTurns,
          maxTokensPerResponse,
          temperature,
          model,
          guardrails:
            Object.keys(guardrails).length > 0 ? guardrails : undefined,
          requireReflection,
          reflectionPrompt: reflectionPrompt || undefined,
        });

        if (response.success && response.data) {
          setConfig(response.data);
          setIsNew(false);
          setSuccessMessage("AI Workspace config created successfully.");
        } else {
          setError(response.error || response.message || "Failed to create config");
        }
      } else if (config) {
        const response = await AiWorkspaceService.updateConfig(config.id, {
          name,
          systemPrompt,
          instructionsForStudent,
          maxTurns,
          maxTokensPerResponse,
          temperature: String(temperature),
          model,
          guardrails:
            Object.keys(guardrails).length > 0 ? guardrails : null,
          requireReflection: requireReflection ? 1 : 0,
          reflectionPrompt,
        } as any);

        if (response.success && response.data) {
          setConfig(response.data);
          setSuccessMessage("Config updated successfully.");
        } else {
          setError(response.error || "Failed to update config");
        }
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!config) {
      setTestError("Save the config first before testing.");
      return;
    }
    if (!testMessage.trim()) {
      setTestError("Enter a test message.");
      return;
    }

    setTesting(true);
    setTestError(null);
    setTestResult(null);

    try {
      const response = await AiWorkspaceService.testConfig(
        config.id,
        testMessage
      );
      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        setTestError(response.error || "Test failed");
      }
    } catch (err) {
      setTestError("Failed to test prompt.");
    } finally {
      setTesting(false);
    }
  };

  // ========== Template Handlers ==========

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await AiWorkspaceService.getTemplates();
      if (response.success && response.data) {
        setTemplates({
          own: response.data.own,
          shared: response.data.shared,
        });
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleOpenSaveTemplateModal = () => {
    setTemplateName(name || "");
    setTemplateDescription("");
    setTemplateIsShared(false);
    setShowSaveTemplateModal(true);
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      setError("Template name is required");
      return;
    }

    setSavingTemplate(true);
    setError(null);

    // Build guardrails object
    const guardrails: any = {};
    if (blockedTopics.trim()) {
      guardrails.blockedTopics = blockedTopics.split(",").map((t) => t.trim()).filter(Boolean);
    }
    if (requiredTopics.trim()) {
      guardrails.requiredTopics = requiredTopics.split(",").map((t) => t.trim()).filter(Boolean);
    }
    if (maxStudentMessageLength) {
      guardrails.maxStudentMessageLength = maxStudentMessageLength;
    }
    guardrails.contentBoundaries = {
      preset: boundaryPreset,
      levels: boundaryPreset === "custom" ? customBoundaries : undefined,
    };

    try {
      const response = await AiWorkspaceService.createTemplate({
        name: templateName,
        description: templateDescription || undefined,
        systemPrompt,
        instructionsForStudent: instructionsForStudent || undefined,
        maxTurns,
        maxTokensPerResponse,
        temperature,
        model,
        guardrails: Object.keys(guardrails).length > 0 ? guardrails : undefined,
        requireReflection,
        reflectionPrompt: reflectionPrompt || undefined,
        isShared: templateIsShared,
      });

      if (response.success) {
        setSuccessMessage("Template saved successfully!");
        setShowSaveTemplateModal(false);
        loadTemplates(); // Refresh templates list
      } else {
        setError(response.error || "Failed to save template");
      }
    } catch (err) {
      setError("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleOpenTemplatePicker = async () => {
    await loadTemplates();
    setShowTemplatePickerModal(true);
  };

  const handleApplyTemplate = async (template: AiPromptConfigTemplate) => {
    // Apply template values to form
    setName(template.name);
    setSystemPrompt(template.systemPrompt);
    setInstructionsForStudent(template.instructionsForStudent || "");
    setMaxTurns(template.maxTurns);
    setMaxTokensPerResponse(template.maxTokensPerResponse);
    setTemperature(parseFloat(template.temperature));
    setModel(template.model);
    setRequireReflection(template.requireReflection === 1);
    setReflectionPrompt(template.reflectionPrompt || "");

    if (template.guardrails) {
      setBlockedTopics((template.guardrails.blockedTopics || []).join(", "));
      setRequiredTopics((template.guardrails.requiredTopics || []).join(", "));
      setMaxStudentMessageLength(template.guardrails.maxStudentMessageLength || 2000);
      if (template.guardrails.contentBoundaries) {
        setBoundaryPreset(template.guardrails.contentBoundaries.preset || "balanced");
        if (template.guardrails.contentBoundaries.levels) {
          setCustomBoundaries(template.guardrails.contentBoundaries.levels);
        }
      }
    }

    setShowTemplatePickerModal(false);
    setSuccessMessage(`Template "${template.name}" applied. Remember to save your config.`);
  };

  const handleSyncFromTemplate = async () => {
    if (!config) return;

    setSyncingFromTemplate(true);
    setError(null);

    try {
      const response = await AiWorkspaceService.syncConfigFromTemplate(config.id);

      if (response.success && response.data) {
        // Reload the config to get updated values
        await loadConfig();
        setShowSyncModal(false);
        setSuccessMessage("Config synced with template successfully!");
      } else {
        setError(response.error || "Failed to sync from template");
      }
    } catch (err) {
      setError("Failed to sync from template");
    } finally {
      setSyncingFromTemplate(false);
    }
  };

  const handleUnlinkTemplate = async () => {
    if (!config) return;

    setUnlinkingTemplate(true);
    setError(null);

    try {
      const response = await AiWorkspaceService.unlinkConfigFromTemplate(config.id);

      if (response.success) {
        setLinkedTemplate(null);
        setTemplateUpdateAvailable(false);
        setSuccessMessage("Config unlinked from template. Your settings will no longer receive template updates.");
      } else {
        setError(response.error || "Failed to unlink from template");
      }
    } catch (err) {
      setError("Failed to unlink from template");
    } finally {
      setUnlinkingTemplate(false);
    }
  };

  const handleAnalyzeConfig = async () => {
    if (!config) {
      setError("Save the config first before analyzing.");
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAiAnalysisIssues([]);
    setAiOverallAssessment(null);

    try {
      const response = await AiWorkspaceService.analyzeConfig(config.id);

      if (response.success && response.data) {
        setAiAnalysisIssues(response.data.issues);
        setAiOverallAssessment(response.data.overallAssessment);
        if (response.data.issues.length === 0) {
          setSuccessMessage("AI analysis complete: No semantic issues found!");
        }
      } else {
        setError(response.error || "Failed to analyze config");
      }
    } catch (err) {
      setError("Failed to analyze config");
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-workspace-config">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    // If we have somewhere to go back to in history, use it; otherwise land
    // the user on the natural parent for this mode so they're never stranded.
    if (window.history.length > 1) {
      history.goBack();
    } else if (isCounselor) {
      history.push("/tenant-preferences");
    } else if (isGlobalMode) {
      history.push("/analytics");
    } else {
      history.push("/");
    }
  };

  return (
    <div className="ai-workspace-config">
      <header className="config-header">
        <div className="header-content">
          <button
            type="button"
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              color: "#4a90d9",
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
              marginBottom: 8,
            }}
          >
            &larr; Back
          </button>
          <h1>{isGlobalMode
            ? (globalConfigType === "generic" ? "AI Assistant Settings"
              : globalConfigType === "resource-default" ? "Default AI Workspace Settings"
              : globalConfigType === "atlas-counselor" ? "Atlas Counselor Settings"
              : "AI Doubt Settings")
            : "AI Workspace Configuration"}</h1>
          <p className="header-subtitle">
            {isGlobalMode
              ? (globalConfigType === "generic"
                ? "Configure the general AI assistant on learning modules."
                : globalConfigType === "resource-default"
                ? "Default AI workspace config for all resources that don't have their own."
                : globalConfigType === "atlas-counselor"
                ? "Configure the Atlas Counselor persona and its knowledge base."
                : "Configure how the AI responds to student doubts across the platform.")
              : <>Configure the AI prompt for: <strong>{resourceName}</strong></>}
          </p>
          {isGlobalMode && (
            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                color: "#5a6478",
              }}
            >
              Tenant-wide model default and API key health live at{" "}
              <a href="/ai-subscriptions" style={{ color: "#4a90d9" }}>
                AI Subscriptions
              </a>
              .
            </div>
          )}
        </div>
        <div className="header-actions">
          {/* Consistency Score Badge */}
          {systemPrompt.trim() && (
            <div className="consistency-score-container">
              <div className="score-row">
                <button
                  className="score-badge"
                  onClick={() => setShowValidationDetails(!showValidationDetails)}
                  style={{ borderColor: getScoreColor(consistencyScore) }}
                >
                  <span
                    className="score-value"
                    style={{ color: getScoreColor(consistencyScore) }}
                  >
                    {consistencyScore}
                  </span>
                  <span className="score-label">{getScoreLabel(consistencyScore)}</span>
                  <span className="score-toggle">{showValidationDetails ? "▲" : "▼"}</span>
                </button>
                <button
                  className="score-info-btn"
                  onClick={() => setShowScoreInfo(!showScoreInfo)}
                  title="How is this score calculated?"
                >
                  ℹ
                </button>
              </div>

              {/* Score Info Tooltip */}
              {showScoreInfo && (
                <div className="score-info-tooltip">
                  <div className="tooltip-header">
                    <span>How This Score is Calculated</span>
                    <button onClick={() => setShowScoreInfo(false)}>×</button>
                  </div>
                  <div className="tooltip-body">
                    <div className="tooltip-section">
                      <strong>Rule-Based Checks (instant):</strong>
                      <ul>
                        {validationIssues.length === 0 ? (
                          <li className="check-pass">✓ All basic checks passed</li>
                        ) : (
                          validationIssues.map((issue) => (
                            <li key={issue.id} className={`check-${issue.severity}`}>
                              {issue.severity === "error" && "✕"}
                              {issue.severity === "warning" && "⚠"}
                              {issue.severity === "info" && "ℹ"} {issue.message.substring(0, 50)}...
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                    <div className="tooltip-section">
                      <strong>AI Analysis (on-demand):</strong>
                      {aiAnalysisIssues.length > 0 ? (
                        <ul>
                          {aiAnalysisIssues.map((issue) => (
                            <li key={issue.id} className={`check-${issue.severity}`}>
                              {issue.severity === "error" && "✕"}
                              {issue.severity === "warning" && "⚠"}
                              {issue.severity === "info" && "ℹ"} {issue.message.substring(0, 50)}...
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-ai-analysis">Click "Analyze" to run AI semantic check</p>
                      )}
                    </div>
                    <div className="tooltip-scoring">
                      <strong>Scoring:</strong> Errors -25, Warnings -10, Info -2
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Dropdown */}
              {showValidationDetails && allIssues.length > 0 && (
                <div className="validation-dropdown">
                  <div className="validation-header">
                    <span>Config Validation</span>
                    <button
                      className="validation-close"
                      onClick={() => setShowValidationDetails(false)}
                    >
                      ×
                    </button>
                  </div>
                  {aiOverallAssessment && (
                    <div className="ai-assessment">
                      <span className="ai-label">AI Assessment:</span>
                      <span className="ai-text">{aiOverallAssessment}</span>
                    </div>
                  )}
                  <div className="validation-issues">
                    {allIssues.map((issue) => (
                      <div
                        key={issue.id}
                        className={`validation-issue issue-${issue.severity} ${issue.isAiIssue ? 'ai-issue' : ''}`}
                      >
                        <span className="issue-icon">
                          {issue.severity === "error" && "✕"}
                          {issue.severity === "warning" && "⚠"}
                          {issue.severity === "info" && "ℹ"}
                        </span>
                        <div className="issue-content">
                          <span className="issue-message">{issue.message}</span>
                          {issue.suggestion && (
                            <span className="issue-suggestion">{issue.suggestion}</span>
                          )}
                          {issue.isAiIssue && <span className="ai-badge">AI</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {showValidationDetails && allIssues.length === 0 && (
                <div className="validation-dropdown">
                  <div className="validation-header">
                    <span>Config Validation</span>
                    <button
                      className="validation-close"
                      onClick={() => setShowValidationDetails(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="validation-success">
                    <span className="success-icon">✓</span>
                    <span>All checks passed! Your configuration looks great.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analyze Button */}
          {systemPrompt.trim() && !isNew && (
            <button
              className="btn-analyze"
              onClick={handleAnalyzeConfig}
              disabled={isAnalyzing}
              title="Run AI semantic analysis"
            >
              {isAnalyzing ? "Analyzing..." : "Analyze"}
            </button>
          )}

          {/* Template Buttons */}
          <button
            className="btn-template-picker"
            onClick={handleOpenTemplatePicker}
            title="Use a template"
          >
            Use Template
          </button>
          {systemPrompt.trim() && (
            <button
              className="btn-save-template"
              onClick={handleOpenSaveTemplateModal}
              title="Save as reusable template"
            >
              Save as Template
            </button>
          )}
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || !systemPrompt.trim()}
          >
            {saving ? "Saving..." : isNew ? "Create Config" : "Update Config"}
          </button>
        </div>
      </header>

      {/* Rubric Info Section */}
      {!isGlobalMode && !rubricLoading && (
        <div className={`rubric-info-banner ${linkedRubric ? 'has-rubric' : 'no-rubric'}`}>
          <div className="rubric-info-content">
            <span className="rubric-icon">{linkedRubric ? '\u{1F4CB}' : '\u26A0'}</span>
            <div className="rubric-details">
              {editingRubric ? (
                <>
                  <strong>Select Rubric:</strong>
                  <select
                    className="rubric-select"
                    value={linkedRubric?.id || ""}
                    onChange={(e) => handleRubricChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={updatingRubric}
                    autoFocus
                  >
                    <option value="">No rubric</option>
                    {rubrics.map((rubric) => (
                      <option key={rubric.id} value={rubric.id}>
                        {rubric.name} ({rubric.maxScore} pts)
                      </option>
                    ))}
                  </select>
                </>
              ) : linkedRubric ? (
                <>
                  <strong>Linked Rubric:</strong> {linkedRubric.name}
                  <span className="rubric-meta">
                    Max Score: {linkedRubric.maxScore} pts | {linkedRubric.criterion?.levels?.length || 0} levels
                  </span>
                </>
              ) : (
                <>
                  <strong>No rubric assigned</strong>
                  <span className="rubric-meta">
                    Assign a rubric to this study resource for AI-powered evaluation
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="rubric-actions">
            {editingRubric ? (
              <button
                className="rubric-link"
                onClick={() => setEditingRubric(false)}
                disabled={updatingRubric}
              >
                Cancel
              </button>
            ) : (
              <button
                className="rubric-link"
                onClick={() => setEditingRubric(true)}
              >
                {linkedRubric ? 'Change Rubric' : 'Assign Rubric'} &rarr;
              </button>
            )}
          </div>
        </div>
      )}

      {/* RAG Status Banner */}
      {!isGlobalMode && !ragLoading && ragStatus && (
        <div className={`rag-info-banner ${
          ragStatus.isRAGified ? 'has-rag' :
          ragStatus.lessonPlans.length === 0 ? 'no-rag' :
          ragStatus.lessonPlans.some(lp => lp.ragStatus === 'processing') ? 'rag-processing' :
          'no-rag'
        }`}>
          <div className="rag-info-content">
            <span className="rag-icon">
              {ragStatus.isRAGified ? '\u{1F9E0}' :
               ragStatus.lessonPlans.length === 0 ? '\u26A0' :
               ragifying ? '\u23F3' : '\u26A0'}
            </span>
            <div className="rag-details">
              {ragStatus.isRAGified ? (
                <>
                  <strong>RAG-ified</strong>
                  <span className="rag-meta">
                    {(() => {
                      const embedded = ragStatus.lessonPlans.find(lp => lp.resourceEmbedded);
                      if (embedded) {
                        return `Embedded in "${embedded.lessonPlanName}" — ${embedded.chunksCreated} chunks, ${embedded.vectorsStored} vectors`;
                      }
                      return 'Resource is embedded in the vector database';
                    })()}
                  </span>
                </>
              ) : ragStatus.lessonPlans.length === 0 ? (
                <>
                  <strong>Not in any lesson plan</strong>
                  <span className="rag-meta">
                    Add this resource to a lesson plan first to enable RAG
                  </span>
                </>
              ) : ragifying ? (
                <>
                  <strong>RAGification in progress...</strong>
                  <span className="rag-meta">
                    This may take a minute depending on resource size
                  </span>
                </>
              ) : (
                <>
                  <strong>Resource not RAG-ified</strong>
                  <span className="rag-meta">
                    AI tutor won't have access to study material content.
                    {ragStatus.lessonPlans.length === 1
                      ? ` Found in "${ragStatus.lessonPlans[0].lessonPlanName}".`
                      : ` Found in ${ragStatus.lessonPlans.length} lesson plans.`}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="rag-actions">
            {ragStatus.isRAGified ? (
              <button
                className="rag-link"
                onClick={loadRagStatus}
              >
                Refresh Status
              </button>
            ) : ragStatus.lessonPlans.length > 0 && !ragifying ? (
              ragStatus.lessonPlans.length === 1 ? (
                <button
                  className="rag-link"
                  onClick={() => handleRagify(ragStatus.lessonPlans[0].lessonPlanId)}
                  disabled={ragifying}
                >
                  RAG-ify Now &rarr;
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    className="rag-select"
                    value={ragifyTargetLpId || ""}
                    onChange={(e) => setRagifyTargetLpId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Select lesson plan...</option>
                    {ragStatus.lessonPlans.map((lp) => (
                      <option key={lp.lessonPlanId} value={lp.lessonPlanId}>
                        {lp.lessonPlanName}
                        {lp.ragStatus ? ` (${lp.ragStatus})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    className="rag-link"
                    onClick={() => ragifyTargetLpId && handleRagify(ragifyTargetLpId)}
                    disabled={!ragifyTargetLpId || ragifying}
                  >
                    RAG-ify Now &rarr;
                  </button>
                </div>
              )
            ) : ragifying ? (
              <span className="rag-spinner"></span>
            ) : null}
          </div>
        </div>
      )}

      {/* Template Update Available Banner */}
      {!isGlobalMode && linkedTemplate && !isNew && (
        <div className={`template-link-banner ${templateUpdateAvailable ? 'has-update' : 'synced'}`}>
          <div className="template-link-content">
            <div className="template-link-icon">
              {templateUpdateAvailable ? '⚠️' : '🔗'}
            </div>
            <div className="template-link-info">
              {templateUpdateAvailable ? (
                <>
                  <strong>Template Update Available</strong>
                  <span className="template-link-meta">
                    "{linkedTemplate.name}" was updated (v{linkedTemplate.version}).
                    Your config is on v{config?.templateVersionApplied || 1}.
                  </span>
                </>
              ) : (
                <>
                  <strong>Linked to Template</strong>
                  <span className="template-link-meta">
                    "{linkedTemplate.name}" (v{linkedTemplate.version}) - Synced
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="template-link-actions">
            {templateUpdateAvailable && (
              <button
                className="btn-sync-template"
                onClick={() => setShowSyncModal(true)}
                disabled={syncingFromTemplate}
              >
                {syncingFromTemplate ? "Syncing..." : "Sync Now"}
              </button>
            )}
            <button
              className="btn-unlink-template"
              onClick={handleUnlinkTemplate}
              disabled={unlinkingTemplate}
              title="Stop receiving updates from this template"
            >
              {unlinkingTemplate ? "Unlinking..." : "Unlink"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      <div className="config-tabs">
        <button
          className={`tab-btn ${activeTab === "config" ? "active" : ""}`}
          onClick={() => setActiveTab("config")}
        >
          Configuration
        </button>
        {!isGlobalMode && (
          <button
            className={`tab-btn ${activeTab === "test" ? "active" : ""}`}
            onClick={() => setActiveTab("test")}
            disabled={isNew}
          >
            Test Prompt
          </button>
        )}
      </div>

      {activeTab === "config" && (
        <div className="config-body">
          <div className="config-grid">
            {/* Left column: Prompts */}
            <div className="config-section">
              <h2>Prompt Design</h2>

              <div className="form-group">
                <label>Config Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Pre-class Critical Thinking Prompt"
                />
              </div>

              <div className="form-group">
                <label>
                  System Prompt <span className="required">*</span>
                  <button
                    type="button"
                    className="btn-use-template"
                    onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                  >
                    {showTemplatePicker ? "Hide templates" : "Choose template"}
                  </button>
                </label>
                <p className="field-help">
                  {isGlobalMode
                    ? (globalConfigType === "generic"
                      ? "Defines the AI's personality and behavior for the general AI assistant on learning modules. Students will not see this."
                      : globalConfigType === "resource-default"
                      ? "Default system prompt for all resources without their own config. Gets cloned per-resource when a student first opens the workspace."
                      : globalConfigType === "atlas-counselor"
                      ? "Defines the Atlas Counselor's tone and scope. RAG context from the configured Knowledge Base lesson plan is appended automatically. Students will not see this."
                      : "Defines the AI's personality and behavior for all \"Ask a Doubt\" conversations. Students will not see this.")
                    : "This is sent to Claude as the system context. Students will not see this. The AI also receives excerpts from the attached study resource (and related learning module materials) via RAG. Use this to define the AI's role, behavior, and pedagogical approach."}
                </p>

                {showTemplatePicker && (
                  <div className="template-picker">
                    {PROMPT_TEMPLATES.map((tpl) => (
                      <div
                        key={tpl.id}
                        className="template-card"
                        onClick={() => {
                          setSystemPrompt(tpl.prompt);
                          setShowTemplatePicker(false);
                        }}
                      >
                        <div className="template-card-name">{tpl.name}</div>
                        <div className="template-card-desc">{tpl.description}</div>
                      </div>
                    ))}
                  </div>
                )}

                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="You are a Socratic tutor helping students understand..."
                  rows={10}
                />
                <span className="char-count">
                  {systemPrompt.length} characters
                </span>
              </div>

              {(!isGlobalMode || isResourceDefault) && (
                <div className="form-group">
                  <label>
                    Instructions for Student
                    {!instructionsForStudent && (
                      <button
                        type="button"
                        className="btn-use-template"
                        onClick={() => setInstructionsForStudent(DEFAULT_STUDENT_INSTRUCTIONS)}
                      >
                        Use template
                      </button>
                    )}
                  </label>
                  <p className="field-help">
                    Visible to students before they start the conversation.
                  </p>
                  <textarea
                    value={instructionsForStudent}
                    onChange={(e) => setInstructionsForStudent(e.target.value)}
                    placeholder="Read the assigned material and then discuss your understanding with the AI tutor..."
                    rows={4}
                  />
                </div>
              )}

              {(!isGlobalMode || isResourceDefault) && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={requireReflection}
                      onChange={(e) => setRequireReflection(e.target.checked)}
                    />
                    Require reflection before submission
                  </label>
                </div>
              )}

              {(!isGlobalMode || isResourceDefault) && requireReflection && (
                <div className="form-group">
                  <label>
                    Reflection Prompt
                    {!reflectionPrompt && (
                      <button
                        type="button"
                        className="btn-use-template"
                        onClick={() => setReflectionPrompt(DEFAULT_REFLECTION_PROMPT)}
                      >
                        Use template
                      </button>
                    )}
                  </label>
                  <textarea
                    value={reflectionPrompt}
                    onChange={(e) => setReflectionPrompt(e.target.value)}
                    placeholder="Reflect on what you learned from this AI conversation. What surprised you? What do you still want to explore?"
                    rows={3}
                  />
                </div>
              )}
            </div>

            {/* Right column: Settings */}
            <div className="config-section">
              <h2>Settings</h2>

              <div className="form-row">
                {(!isGlobalMode || isResourceDefault) && (
                  <div className="form-group half">
                    <label>Max Turns</label>
                    <input
                      type="number"
                      value={maxTurns}
                      onChange={(e) => setMaxTurns(Number(e.target.value))}
                      min={1}
                      max={50}
                    />
                    <span className="field-help">
                      How many messages the student can send
                    </span>
                  </div>
                )}
                <div className="form-group half">
                  <label>Max Tokens per Response</label>
                  <input
                    type="number"
                    value={maxTokensPerResponse}
                    onChange={(e) =>
                      setMaxTokensPerResponse(Number(e.target.value))
                    }
                    min={256}
                    max={8192}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>Temperature</label>
                  <input
                    type="number"
                    step="0.1"
                    value={temperature}
                    onChange={(e) => setTemperature(Number(e.target.value))}
                    min={0}
                    max={1}
                  />
                  <span className="field-help">
                    0 = deterministic, 1 = creative
                  </span>
                </div>
                <div className="form-group half">
                  <label>Model</label>
                  <select
                    value={model || ""}
                    onChange={(e) => setModel(e.target.value)}
                  >
                    <option value="">Use tenant default (set at AI Subscriptions)</option>
                    {supportedModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                    {model && !supportedModels.some((m) => m.id === model) && (
                      <option value={model} disabled>
                        {model} — deprecated, please choose a current model
                      </option>
                    )}
                  </select>
                  <span className="field-help">
                    Pin a specific model, or leave on "Use tenant default" to inherit from{" "}
                    <a href="/ai-subscriptions" target="_blank" rel="noreferrer">
                      AI Subscriptions
                    </a>
                    .
                  </span>
                  {model && !supportedModels.some((m) => m.id === model) && (
                    <span className="field-help" style={{ color: "#b54708" }}>
                      The currently saved model is no longer supported. Pick a
                      current option above before saving.
                    </span>
                  )}
                </div>
              </div>

              {isCounselor && (
                <div className="form-group">
                  <label>Knowledge Base Lesson Plan ID</label>
                  <p className="field-help">
                    The Learning Module whose study resources are RAG-indexed and used to ground counselor answers.
                    Create the LM under <a href="/learning-modules" target="_blank" rel="noreferrer">Learning Modules</a>,
                    add resources, then open it in the LM Builder and click "RAGify" to index the content.
                  </p>
                  <input
                    type="number"
                    value={knowledgeBaseLessonPlanId}
                    onChange={(e) =>
                      setKnowledgeBaseLessonPlanId(
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    placeholder="e.g. 1591"
                    min={1}
                  />
                  {knowledgeBaseLessonPlanId !== "" && (
                    <a
                      href={`/lm-builder/${knowledgeBaseLessonPlanId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: "inline-block", marginTop: 6, fontSize: 13 }}
                    >
                      Open LM {knowledgeBaseLessonPlanId} in builder &rarr;
                    </a>
                  )}
                </div>
              )}

              <h2 className="section-divider">Guardrails</h2>

              <div className="form-group">
                <label>
                  Blocked Topics
                  {!blockedTopics && (
                    <button
                      type="button"
                      className="btn-use-template"
                      onClick={() => setBlockedTopics("homework answers, exam solutions, direct code solutions, plagiarism assistance")}
                    >
                      Use template
                    </button>
                  )}
                </label>
                <p className="field-help">
                  Comma-separated list of topics to block in student messages.
                </p>
                <input
                  type="text"
                  value={blockedTopics}
                  onChange={(e) => setBlockedTopics(e.target.value)}
                  placeholder="e.g., homework answers, exam solutions"
                />
              </div>

              <div className="form-group">
                <label>
                  Required Topics
                  {!requiredTopics && (
                    <button
                      type="button"
                      className="btn-use-template"
                      onClick={() => setRequiredTopics("critical analysis, evidence-based reasoning, key concepts from the material")}
                    >
                      Use template
                    </button>
                  )}
                </label>
                <p className="field-help">
                  Topics the AI should steer the conversation towards.
                </p>
                <input
                  type="text"
                  value={requiredTopics}
                  onChange={(e) => setRequiredTopics(e.target.value)}
                  placeholder="e.g., critical analysis, evidence-based reasoning"
                />
              </div>

              <div className="form-group">
                <label>Max Student Message Length</label>
                <input
                  type="number"
                  value={maxStudentMessageLength}
                  onChange={(e) =>
                    setMaxStudentMessageLength(Number(e.target.value))
                  }
                  min={100}
                  max={10000}
                />
              </div>

              {/* Content Boundaries Section */}
              <div className="section-divider">
                <h2>Content Boundaries</h2>
                <p className="field-help">
                  Configure how the AI responds to questions at different scope levels.
                </p>
              </div>

              <div className="form-group">
                <label>Boundary Style</label>
                <select
                  className="select"
                  value={boundaryPreset}
                  onChange={(e) => setBoundaryPreset(e.target.value as BoundaryPreset)}
                >
                  <option value="strict">Strict Focus (Exam mode)</option>
                  <option value="balanced">Balanced Learning (Recommended)</option>
                  <option value="exploratory">Exploratory</option>
                  <option value="custom">Custom...</option>
                </select>
                <p className="field-help">
                  {boundaryPreset === "strict" && "Only allows questions about the specific resource. Firmly redirects off-topic questions."}
                  {boundaryPreset === "balanced" && "Allows resource and module questions. Gently redirects unrelated topics."}
                  {boundaryPreset === "exploratory" && "Allows most subject-related questions. Softly guides back for unrelated topics."}
                  {boundaryPreset === "custom" && "Configure each scope level individually below."}
                </p>
              </div>

              {/* Compact summary grid for presets */}
              {boundaryPreset !== "custom" && BOUNDARY_PRESETS[boundaryPreset] && (
                <div className="boundary-summary-grid">
                  <div className="boundary-summary-cell">
                    <div className="summary-level">Within Resource</div>
                    <div className={`summary-behavior behavior-${BOUNDARY_PRESETS[boundaryPreset].withinResource.behavior}`}>
                      {BOUNDARY_PRESETS[boundaryPreset].withinResource.behavior === "allow" && "✓ Allow"}
                      {BOUNDARY_PRESETS[boundaryPreset].withinResource.behavior === "redirect" && "↩ Redirect"}
                      {BOUNDARY_PRESETS[boundaryPreset].withinResource.behavior === "block" && "✕ Block"}
                    </div>
                    <div className="summary-tone">{BOUNDARY_PRESETS[boundaryPreset].withinResource.tone}</div>
                  </div>
                  <div className="boundary-summary-cell">
                    <div className="summary-level">Within Module</div>
                    <div className={`summary-behavior behavior-${BOUNDARY_PRESETS[boundaryPreset].withinModule.behavior}`}>
                      {BOUNDARY_PRESETS[boundaryPreset].withinModule.behavior === "allow" && "✓ Allow"}
                      {BOUNDARY_PRESETS[boundaryPreset].withinModule.behavior === "redirect" && "↩ Redirect"}
                      {BOUNDARY_PRESETS[boundaryPreset].withinModule.behavior === "block" && "✕ Block"}
                    </div>
                    <div className="summary-tone">{BOUNDARY_PRESETS[boundaryPreset].withinModule.tone}</div>
                  </div>
                  <div className="boundary-summary-cell">
                    <div className="summary-level">Subject Related</div>
                    <div className={`summary-behavior behavior-${BOUNDARY_PRESETS[boundaryPreset].subjectRelated.behavior}`}>
                      {BOUNDARY_PRESETS[boundaryPreset].subjectRelated.behavior === "allow" && "✓ Allow"}
                      {BOUNDARY_PRESETS[boundaryPreset].subjectRelated.behavior === "redirect" && "↩ Redirect"}
                      {BOUNDARY_PRESETS[boundaryPreset].subjectRelated.behavior === "block" && "✕ Block"}
                    </div>
                    <div className="summary-tone">{BOUNDARY_PRESETS[boundaryPreset].subjectRelated.tone}</div>
                  </div>
                  <div className="boundary-summary-cell">
                    <div className="summary-level">Unrelated</div>
                    <div className={`summary-behavior behavior-${BOUNDARY_PRESETS[boundaryPreset].unrelated.behavior}`}>
                      {BOUNDARY_PRESETS[boundaryPreset].unrelated.behavior === "allow" && "✓ Allow"}
                      {BOUNDARY_PRESETS[boundaryPreset].unrelated.behavior === "redirect" && "↩ Redirect"}
                      {BOUNDARY_PRESETS[boundaryPreset].unrelated.behavior === "block" && "✕ Block"}
                    </div>
                    <div className="summary-tone">{BOUNDARY_PRESETS[boundaryPreset].unrelated.tone}</div>
                  </div>
                </div>
              )}

              {boundaryPreset === "custom" && (
                <div className="boundary-levels">
                  {/* Level 1: Within Resource */}
                  <div className="boundary-card">
                    <div className="boundary-header">
                      <h4>Level 1: Within This Resource</h4>
                      <p>Questions directly about the PDF/content being studied</p>
                    </div>
                    <div className="boundary-controls">
                      <div className="control-group">
                        <label>Response</label>
                        <select
                          value={customBoundaries.withinResource.behavior}
                          onChange={(e) => setCustomBoundaries({
                            ...customBoundaries,
                            withinResource: { ...customBoundaries.withinResource, behavior: e.target.value as BoundaryBehavior }
                          })}
                        >
                          <option value="allow">Allow</option>
                          <option value="redirect">Redirect</option>
                          <option value="block">Block</option>
                        </select>
                      </div>
                      {customBoundaries.withinResource.behavior !== "allow" && (
                        <>
                          <div className="control-group">
                            <label>Tone</label>
                            <select
                              value={customBoundaries.withinResource.tone}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                withinResource: { ...customBoundaries.withinResource, tone: e.target.value as BoundaryTone }
                              })}
                            >
                              <option value="encouraging">Encouraging</option>
                              <option value="gentle">Gentle</option>
                              <option value="neutral">Neutral</option>
                              <option value="firm">Firm</option>
                            </select>
                          </div>
                          <div className="control-group full-width">
                            <label>Custom Message (optional)</label>
                            <input
                              type="text"
                              value={customBoundaries.withinResource.customMessage || ""}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                withinResource: { ...customBoundaries.withinResource, customMessage: e.target.value }
                              })}
                              placeholder="Custom redirect message..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Level 2: Within Module */}
                  <div className="boundary-card">
                    <div className="boundary-header">
                      <h4>Level 2: Within Learning Module</h4>
                      <p>Questions about other resources in the same lesson plan</p>
                    </div>
                    <div className="boundary-controls">
                      <div className="control-group">
                        <label>Response</label>
                        <select
                          value={customBoundaries.withinModule.behavior}
                          onChange={(e) => setCustomBoundaries({
                            ...customBoundaries,
                            withinModule: { ...customBoundaries.withinModule, behavior: e.target.value as BoundaryBehavior }
                          })}
                        >
                          <option value="allow">Allow</option>
                          <option value="redirect">Redirect</option>
                          <option value="block">Block</option>
                        </select>
                      </div>
                      {customBoundaries.withinModule.behavior !== "allow" && (
                        <>
                          <div className="control-group">
                            <label>Tone</label>
                            <select
                              value={customBoundaries.withinModule.tone}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                withinModule: { ...customBoundaries.withinModule, tone: e.target.value as BoundaryTone }
                              })}
                            >
                              <option value="encouraging">Encouraging</option>
                              <option value="gentle">Gentle</option>
                              <option value="neutral">Neutral</option>
                              <option value="firm">Firm</option>
                            </select>
                          </div>
                          <div className="control-group full-width">
                            <label>Custom Message (optional)</label>
                            <input
                              type="text"
                              value={customBoundaries.withinModule.customMessage || ""}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                withinModule: { ...customBoundaries.withinModule, customMessage: e.target.value }
                              })}
                              placeholder="Custom redirect message..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Level 3: Subject Related */}
                  <div className="boundary-card">
                    <div className="boundary-header">
                      <h4>Level 3: Subject-Related, Off-Curriculum</h4>
                      <p>Valid subject questions not covered in current materials</p>
                    </div>
                    <div className="boundary-controls">
                      <div className="control-group">
                        <label>Response</label>
                        <select
                          value={customBoundaries.subjectRelated.behavior}
                          onChange={(e) => setCustomBoundaries({
                            ...customBoundaries,
                            subjectRelated: { ...customBoundaries.subjectRelated, behavior: e.target.value as BoundaryBehavior }
                          })}
                        >
                          <option value="allow">Allow</option>
                          <option value="redirect">Redirect</option>
                          <option value="block">Block</option>
                        </select>
                      </div>
                      {customBoundaries.subjectRelated.behavior !== "allow" && (
                        <>
                          <div className="control-group">
                            <label>Tone</label>
                            <select
                              value={customBoundaries.subjectRelated.tone}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                subjectRelated: { ...customBoundaries.subjectRelated, tone: e.target.value as BoundaryTone }
                              })}
                            >
                              <option value="encouraging">Encouraging</option>
                              <option value="gentle">Gentle</option>
                              <option value="neutral">Neutral</option>
                              <option value="firm">Firm</option>
                            </select>
                          </div>
                          <div className="control-group full-width">
                            <label>Custom Message (optional)</label>
                            <input
                              type="text"
                              value={customBoundaries.subjectRelated.customMessage || ""}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                subjectRelated: { ...customBoundaries.subjectRelated, customMessage: e.target.value }
                              })}
                              placeholder="e.g., Great curiosity! That topic is covered in Module 4..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Level 4: Unrelated */}
                  <div className="boundary-card">
                    <div className="boundary-header">
                      <h4>Level 4: Completely Unrelated</h4>
                      <p>Off-topic questions unrelated to the subject</p>
                    </div>
                    <div className="boundary-controls">
                      <div className="control-group">
                        <label>Response</label>
                        <select
                          value={customBoundaries.unrelated.behavior}
                          onChange={(e) => setCustomBoundaries({
                            ...customBoundaries,
                            unrelated: { ...customBoundaries.unrelated, behavior: e.target.value as BoundaryBehavior }
                          })}
                        >
                          <option value="allow">Allow</option>
                          <option value="redirect">Redirect</option>
                          <option value="block">Block</option>
                        </select>
                      </div>
                      {customBoundaries.unrelated.behavior !== "allow" && (
                        <>
                          <div className="control-group">
                            <label>Tone</label>
                            <select
                              value={customBoundaries.unrelated.tone}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                unrelated: { ...customBoundaries.unrelated, tone: e.target.value as BoundaryTone }
                              })}
                            >
                              <option value="encouraging">Encouraging</option>
                              <option value="gentle">Gentle</option>
                              <option value="neutral">Neutral</option>
                              <option value="firm">Firm</option>
                            </select>
                          </div>
                          <div className="control-group full-width">
                            <label>Custom Message (optional)</label>
                            <input
                              type="text"
                              value={customBoundaries.unrelated.customMessage || ""}
                              onChange={(e) => setCustomBoundaries({
                                ...customBoundaries,
                                unrelated: { ...customBoundaries.unrelated, customMessage: e.target.value }
                              })}
                              placeholder="e.g., I'm here to help with Biology. Let's get back to..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "test" && (
        <div className="test-body">
          <div className="test-layout">
            <div className="test-input-section">
              <h2>Test Your Prompt</h2>
              <p className="field-help">
                Send a sample student message to see how the AI responds with
                your current configuration.
              </p>
              <div className="form-group">
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Type a sample student message..."
                  rows={4}
                />
              </div>
              <button
                className="btn-test"
                onClick={handleTest}
                disabled={testing || !testMessage.trim()}
              >
                {testing ? "Testing..." : "Send Test Message"}
              </button>
            </div>

            <div className="test-result-section">
              <h2>AI Response</h2>
              {testError && (
                <div className="alert alert-error">{testError}</div>
              )}
              {testResult ? (
                <div className="test-result">
                  <div className="response-text">{testResult.response}</div>
                  <div className="response-meta">
                    <span>
                      Tokens: {testResult.tokenUsage?.inputTokens || 0} in /{" "}
                      {testResult.tokenUsage?.outputTokens || 0} out
                    </span>
                    <span>Time: {testResult.processingTimeMs}ms</span>
                  </div>
                </div>
              ) : (
                <div className="test-placeholder">
                  <p>Send a test message to see the AI response here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showSaveTemplateModal && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowSaveTemplateModal(false)}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="save-template-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="save-template-title">Save as Template</h2>
              <button
                className="modal-close"
                onClick={() => setShowSaveTemplateModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Template Name *</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Socratic Biology Tutor"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description of when to use this template..."
                  rows={3}
                />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={templateIsShared}
                    onChange={(e) => setTemplateIsShared(e.target.checked)}
                  />
                  <span>Share with other teachers</span>
                </label>
                <p className="field-help">
                  If enabled, other teachers can use this template for their resources.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowSaveTemplateModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate || !templateName.trim()}
              >
                {savingTemplate ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePickerModal && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowTemplatePickerModal(false)}>
          <div className="modal-content template-picker-modal" role="dialog" aria-modal="true" aria-labelledby="template-picker-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="template-picker-title">Choose a Template</h2>
              <button
                className="modal-close"
                onClick={() => setShowTemplatePickerModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingTemplates ? (
                <div className="loading-templates">
                  <div className="spinner" />
                  <p>Loading templates...</p>
                </div>
              ) : (
                <>
                  {templates.own.length > 0 && (
                    <div className="template-section">
                      <h3>Your Templates</h3>
                      <div className="template-grid">
                        {templates.own.map((template) => (
                          <div
                            key={template.id}
                            className="template-card"
                            onClick={() => handleApplyTemplate(template)}
                          >
                            <div className="template-name">{template.name}</div>
                            {template.description && (
                              <div className="template-description">
                                {template.description}
                              </div>
                            )}
                            <div className="template-meta">
                              <span>Used {template.usageCount} times</span>
                              {template.isShared === 1 && (
                                <span className="shared-badge">Shared</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {templates.shared.length > 0 && (
                    <div className="template-section">
                      <h3>Shared Templates</h3>
                      <div className="template-grid">
                        {templates.shared.map((template) => (
                          <div
                            key={template.id}
                            className="template-card"
                            onClick={() => handleApplyTemplate(template)}
                          >
                            <div className="template-name">{template.name}</div>
                            {template.description && (
                              <div className="template-description">
                                {template.description}
                              </div>
                            )}
                            <div className="template-meta">
                              <span>Used {template.usageCount} times</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {templates.own.length === 0 && templates.shared.length === 0 && (
                    <div className="no-templates">
                      <p>No templates available yet.</p>
                      <p className="field-help">
                        Create a config and save it as a template to reuse it later.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowTemplatePickerModal(false)}
              >
                Start from Scratch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Confirmation Modal */}
      {showSyncModal && linkedTemplate && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content sync-modal" role="dialog" aria-modal="true" aria-labelledby="sync-template-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="sync-template-title">Sync from Template?</h2>
              <button
                className="modal-close"
                onClick={() => setShowSyncModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="sync-intro">
                This will update your config with the latest settings from template
                <strong> "{linkedTemplate.name}"</strong>.
              </p>
              <div className="sync-version-info">
                <div className="version-row">
                  <span className="version-label">Template Version:</span>
                  <span className="version-value version-new">v{linkedTemplate.version}</span>
                </div>
                <div className="version-row">
                  <span className="version-label">Your Config Version:</span>
                  <span className="version-value version-old">v{config?.templateVersionApplied || 1}</span>
                </div>
              </div>
              <div className="sync-warning">
                <strong>Note:</strong> The following settings will be updated:
                <ul>
                  <li>System Prompt</li>
                  <li>Instructions for Student</li>
                  <li>Max Turns, Tokens, Temperature, Model</li>
                  <li>Guardrails and Content Boundaries</li>
                  <li>Reflection Settings</li>
                </ul>
                <p className="warning-text">
                  Your local customizations will be overwritten. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowSyncModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-sync-confirm"
                onClick={handleSyncFromTemplate}
                disabled={syncingFromTemplate}
              >
                {syncingFromTemplate ? "Syncing..." : "Sync Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiWorkspaceConfig;
